import React, { useEffect, useState, useRef } from "react";
import Info_SVG from "../../assets/information.svg";
import COPY from "../../assets/copy.svg";
// import Markdown from "react-markdown";
// import remarkGfm from "remark-gfm";
// import remarkToc from "remark-toc";

import { FaThumbsDown } from "react-icons/fa6";
import { FaThumbsUp } from "react-icons/fa6";
import FeedbackModal from "../Feedback/FeedbackModal";
import { HiSpeakerWave } from "react-icons/hi2";
import { TbPlayerTrackPrevFilled } from "react-icons/tb";
import { IoIosSquare } from "react-icons/io";
import { useMsal } from "@azure/msal-react";

import {
  startSpeech,
  stopSpeech,
  seekBackward,
} from "../../utils/services/speechService";
import { handleDislikeMsg, handleLikeMsg } from "../../utils/services/api";
import { MODEL_LIST } from "../../utils/services/api";
import ModelDropdown from "../ModelDropdown/ModelDropdown";
import TypingIndicator from "../TypingIndicator/TypingIndicator";

import toast from "react-hot-toast";

const BotMessage = ({
  msg,
  handleOpenCitation,
  conversationId,
  isMultiChatRunning,
  messages,
  refreshLikeState,
  onSetActiveModel,
  isLastMessage,
  scrollRef,
  scrollToBottom,
}) => {
  const [copied, setCopied] = useState(false);

  // Phase-3 multi-model: msg.modelResponses holds one entry per model in MODEL_LIST,
  // keyed by model value, e.g. { status: "pending"|"done"|"error", answer, citations, ... }.
  // msg.activeModel is whichever model's response is currently being displayed.
  // Legacy single-model chat history has no modelResponses, so it just falls back to msg itself.
  const isMultiModel = !!msg?.modelResponses;
  const activeModelData = isMultiModel
    ? msg.modelResponses?.[msg.activeModel]
    : null;
  const effectiveMsg = isMultiModel
    ? {
        message: activeModelData?.answer,
        citations: activeModelData?.citations,
        best_match: activeModelData?.best_match,
        category: activeModelData?.category,
        sub_category: activeModelData?.sub_category,
        legal_description: activeModelData?.legal_description,
        state: activeModelData?.state,
      }
    : msg;
  // "done" was previously the ONLY status that ever cleared the typing
  // indicator — a model call that errors out (network blip, timeout, 5xx)
  // never reaches "done", so that bubble was stuck on the bouncing dots
  // forever even though the request itself had long since settled (see
  // api.js's multiChatAllModels, which sets status:"error" via Promise.allSettled
  // rather than ever throwing). anyModelSettled additionally treats "error" as
  // a terminal state so the bubble always resolves to *something* visible.
  const anyModelSettled =
    isMultiModel &&
    Object.values(msg.modelResponses || {}).some(
      (m) => m?.status === "done" || m?.status === "error"
    );
  const activeModelErrored = isMultiModel && activeModelData?.status === "error";

  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const { accounts } = useMsal();
  const [feedbackType, setFeedbackType] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const userData = accounts[0];
  const timeoutRef = useRef(null);
  const copyTimeoutRef = useRef(null);


  const handleSpeak = () => {
    let htmlContent = effectiveMsg?.message;

    if (!htmlContent) {
      console.warn("No text found for the selected model.");
      return;
    }

    // Create a temporary DOM element
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    // Optionally replace <br> and <li> with periods for natural pauses
    tempDiv.querySelectorAll("br, li").forEach((el) => {
      el.insertAdjacentText("beforebegin", ". ");
    });

    // Get the text content
    let textToSpeak = tempDiv.textContent || tempDiv.innerText || "";

    // Remove extra whitespace
    textToSpeak = textToSpeak.replace(/\s+/g, " ").trim();

    setIsLoading(true);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      startSpeech(
        textToSpeak,
        () => {
          setIsLoading(false);
          setSpeaking(true);
        },
        () => {
          setSpeaking(false);
          setIsLoading(false);
        }
      );
    }, 1000);
  };

  const handleStop = () => {
    stopSpeech();
    setSpeaking(false);
    setIsLoading(false);
  };

  const handleLike = async () => {
    try {
      if (disliked) setDisliked(false);

      const likeData = {
        user_id: messages?.user_id,
        session_id: messages?.session_id,
        message_id: msg?.msg_id_log?.msg_id,
      };

      await handleLikeMsg(likeData);

      refreshLikeState(msg?.msg_id_log?.msg_id, true);
    } catch (err) {
      console.error("Like failed:", err);
      toast.error("Failed to send like feedback");
    }
  };

  const handleDislike = async () => {
    // Directly submits feedback the moment thumbs-down is clicked — no
    // "Incorrect response"/"Improve response" choice, no message box.
    if (liked) setLiked(false);
    setDisliked(true);

    try {
      const feedbackData = {
        user_id: messages?.user_id,
        session_id: messages?.session_id,
        message_id: msg?.msg_id_log?.msg_id,
        feedback: "thumbs_down",
        email: userData?.username,
        name: userData?.idTokenClaims?.given_name,
        state: userData?.idTokenClaims?.extension_NECAAccountChapter,
        family_name: userData?.idTokenClaims?.family_name,
        category: effectiveMsg?.category,
        feedback_type: "NikiSuggest",
      };

      await handleDislikeMsg(feedbackData);

      refreshLikeState(msg?.msg_id_log?.msg_id, false);
    } catch (err) {
      console.error("Dislike failed:", err);
      toast.error("Failed to send dislike feedback");
    }
  };

  // Optional cleanup on unmount
  const handleCopyFile = (msg) => {
    try {
      // Convert HTML string to plain text
      const tempElement = document.createElement("div");
      tempElement.innerHTML = msg?.message || "";
      const plainText = tempElement.innerText;

      navigator.clipboard.writeText(plainText).then(() => {
        setCopied(true);

        // Clear previous timeout
        if (copyTimeoutRef.current) {
          clearTimeout(copyTimeoutRef.current);
        }

        // Reset copied state after 1s
        copyTimeoutRef.current = setTimeout(() => setCopied(false), 1000);
      });
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmitModal = async (improvementText) => {
    try {
      setShowModal(false); // close modal
      setDisliked(true);
      setLiked(false);

      const feedbackData = {
        user_id: messages?.user_id,
        session_id: messages?.session_id,
        message_id: msg?.msg_id_log?.msg_id,
        feedback: "thumbs_down",
        comment: feedback, // optional
        email: userData?.username,
        name: userData?.idTokenClaims?.given_name,
        state: userData?.idTokenClaims?.extension_NECAAccountChapter,
        family_name: userData?.idTokenClaims?.family_name,
        category: effectiveMsg?.category,
        feedback_type: feedbackType ? "NikiCase" : "NikiSuggest",
      };

      await handleDislikeMsg(feedbackData);

      setFeedback("");

      refreshLikeState(msg?.msg_id_log?.msg_id, false);
    } catch (err) {
      console.error("Dislike failed:", err);
      toast.error("Failed to send dislike feedback");
    }
  };

  const handleCloseModal = () => {
    setFeedback("");
    setShowModal(false);
    setDisliked(false);
  };

  const rawMessage = effectiveMsg?.message;
  const normalizedMsg =
    typeof rawMessage === "string"
      ? rawMessage.replace(/[“”]/g, '"').trim()
      : "";

  useEffect(() => {
    if (msg?.like_state === true) {
      setLiked(true);
      setDisliked(false);
    } else if (msg?.like_state === false) {
      setLiked(false);
      setDisliked(true);
    } else {
      setLiked(false);
      setDisliked(false);
    }
  }, [msg?.like_state]);

  const openModal = (type) => {
    setFeedbackType(type); // Set the feedback type first
    setShowModal(true); // Then open the modal
  };

  const citationsArray = Array.isArray(effectiveMsg?.citations)
    ? effectiveMsg.citations
    : effectiveMsg?.citations
    ? [effectiveMsg.citations]
    : [];

  const fallbackMsg =
    "I'm sorry, but I couldn't find the information you're looking for. Please rephrase your question or contact our specialist team at 1300 361 099.";
  const defaultMessage = "Something went wrong";

  const rawMessage2 = effectiveMsg?.message;
  const normalizedMsg2 =
    typeof rawMessage2 === "string"
      ? rawMessage2.replace(/[“”]/g, '"').trim()
      : "";

  // While the answer is still being generated, reserve the disclaimer's space
  // (rather than not rendering it) so the response card doesn't visibly grow
  // once the disclaimer appears — it just stays invisible until content is done.
  const isLoadingContent = isMultiModel ? !anyModelSettled : normalizedMsg.length === 0;
  const isErrorLikeMsg =
    normalizedMsg === fallbackMsg ||
    normalizedMsg2 === defaultMessage ||
    activeModelErrored;


  // const chatArray = Array.isArray(messages?.chat_history)
  //   ? messages.chat_history
  //   : [];

  // // Helper to get a stable ID (fallbacks if missing)
  // const getMsgId = (m) => m?.msg_id_log?.msg_id ?? m?._id ?? m?.id ?? null;

  // // Find the index of the first Legal message
  // const firstLegalIndex = chatArray.findIndex(
  //   (m) => m?.sub_category === "Legal"
  // );

  // // Find the index of the current message
  // const currentIndex = chatArray.findIndex(
  //   (m) => getMsgId(m) === getMsgId(msg)
  // );

  // // True only if THIS msg is the first Legal one
  // const isFirstLegalMsg =
  //   msg?.sub_category === "Legal" &&
  //   firstLegalIndex !== -1 &&
  //   currentIndex === firstLegalIndex;

  const chatArray = Array.isArray(messages?.chat_history)
  ? messages.chat_history
  : [];

// Helper to get a stable ID (fallbacks if missing)
const getMsgId = (m) => m?.msg_id_log?.msg_id ?? m?._id ?? m?.id ?? null;

// Categories to treat as "Legal-type"
const legalCategories = ["Legal", "Workplace relations"];

// A message's sub_category lives flat on legacy single-model history,
// or nested under the active model's response for new multi-model messages.
const getSubCategory = (m) =>
  m?.modelResponses ? m.modelResponses?.[m.activeModel]?.sub_category : m?.sub_category;

// Find the index of the first Legal-type message
const firstLegalIndex = chatArray.findIndex((m) =>
  legalCategories.includes(getSubCategory(m))
);

// Find the index of the current message
const currentIndex = chatArray.findIndex(
  (m) => getMsgId(m) === getMsgId(msg)
);

// True only if THIS msg is the first Legal-type one
const isFirstLegalMsg =
  legalCategories.includes(effectiveMsg?.sub_category) &&
  firstLegalIndex !== -1 &&
  currentIndex === firstLegalIndex;


  return (
    <div className="msg bot-msg max-w-[85%] flex-end  border-[1px] border-[#D0F2FF] p-[0.4rem] md:p-[0.6rem] px-2 md:px-4 rounded-[4px] relative mb-[40px] bg-white">
      {showModal && (
        <FeedbackModal
          isOpen={showModal}
          msg={msg}
          messages={messages}
          onClose={handleCloseModal}
          onSubmit={handleSubmitModal}
          feedback={feedback}
          setFeedback={setFeedback}
          type={feedbackType}
        />
      )}
      <div className="w-full bg-white]">
        {isMultiModel && MODEL_LIST.length > 1 && (
          <div className="mb-2">
            <ModelDropdown
              modelslist={MODEL_LIST}
              models={msg.modelResponses}
              activeModel={MODEL_LIST.find((m) => m.value === msg.activeModel)}
              setActiveModel={(option) => onSetActiveModel?.(msg, option?.value)}
            />
          </div>
        )}

        {isFirstLegalMsg && (
          <div className="text-[#424242] text-sm bg-white mb-2">
            <p>{effectiveMsg?.legal_description}</p>
          </div>
        )}

        {isMultiModel && !anyModelSettled ? (
          <TypingIndicator />
        ) : (
        <div className="text-[#424242] text-sm bg-white">
          <p className="flex flex-wrap items-center gap-1">
            {activeModelErrored ? (
              <span>{defaultMessage} — please try again.</span>
            ) : (
              <div
                className="markdown-body table-wrapper overflow-x-auto"
                dangerouslySetInnerHTML={{ __html: effectiveMsg?.message }}
                // dangerouslySetInnerHTML={{ __html: apiResponse }}
              />
            )}

            {normalizedMsg === fallbackMsg && (
              <span className="text-sm text-[#414141]">
                or click{" "}
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    openModal(true);
                  }}
                  className="hover:underline text-[#00AEEF] font-semibold cursor-pointer"
                >
                  Here
                </span>{" "}
                to message them now.
              </span>
            )}
          </p>
        </div>
        )}

        {![
          fallbackMsg,
          defaultMessage,
          "Hello, I am Niki, an AI assistant designed to assist users with their questions about electrical work and regulations in Australia. How can I help you today?",
        ].includes(normalizedMsg2) &&
          effectiveMsg?.state?.length > 0 &&
          (() => {
            const filteredStates = effectiveMsg.state.filter(
              (s) => s.toLowerCase() !== "national" && s.trim() !== ""
            );
            return filteredStates.length > 0 ? (
              <p className="text-sm text-[#424242] mt-2">
                <strong>Please note:</strong> This information is specific to{" "}
                {filteredStates.join(", ")}
              </p>
            ) : null;
          })()}

        {citationsArray.length > 0 && (
          <div className="w-full flex  sm:flex md:flex-row items-center md:items-center md:justify-start gap-0.5 md:gap-4 mt-2 flex-wrap">
            <p className="text-[12px] text-[#242424] font-semibold">
              Reference Link :
            </p>
            {citationsArray.map((citation, i) => {
              const displayName = citation?.name?.trim()
                ? citation.name
                : `Source-${i + 1}`;

              return citation.type === "citation" ? (
                <div
                  key={i}
                  onClick={() => handleOpenCitation(citation, i)}
                  className="min-w-[6rem] ellipsis-text-2 flex items-center justify-center border-[1px] border-[#00AEEF] text-[#00AEEF] hover:text-white hover:bg-[#00AEEF] py-[0.23rem] px-2 text-sm rounded-full cursor-pointer font-[600] transition-colors duration-500 w-full md:w-fit text-center md:text-start"
                >
                  {`Source-${i + 1}`}
                </div>
              ) : (
                <a
                  key={i}
                  href={citation.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[12px] text-[#005BAA] cursor-pointer hover:underline font-semibold"
                >
                  {displayName}
                </a>
              );
            })}
          </div>
        )}
        {effectiveMsg?.best_match?.title?.length > 0 && (
          <>
            <hr className="border-[1px] border-[#E2E2E2] mt-3 mb-1" />
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#424242] font-[800]">Related to:</p>
              <div className="w-24 flex items-center justify-center border-[1px] border-[#00AEEF] text-[#00AEEF] py-[0.1rem] text-sm rounded-full font-[600]">
                <p className="mb-[3px]">Sponsor</p>
              </div>
            </div>
            <a
              className="ellipsis-text-2 flex items-center justify-center text-[#00AEEF] py-[0.23rem] pe-[4px] ps-[2px] text-sm rounded-full cursor-pointer font-[600] transition-colors duration-500 w-full md:w-fit text-center md:text-start"
              href={effectiveMsg?.best_match?.url}
              target="_blank"
              rel="noreferrer"
            >
              {effectiveMsg?.best_match?.title}
            </a>
          </>
        )}
        {effectiveMsg?.best_match?.body?.length > 0 && (
          <div className="p-2 mt-2 bg-[#fafafa] border-[1px] border-[#888888] rounded-md">
            <p
              style={{
                fontFamily: `"TStarProHeavy", sans-serif`,
              }}
              className="text-[#414141] text-[11px] whitespace-pre-line font-[800]"
            >
              {effectiveMsg?.best_match?.body?.split("\n")[0]}
            </p>
            <p className="text-[#414141] text-[11px] whitespace-pre-line mt-[2px]">
              {effectiveMsg?.best_match?.body?.split("\n")?.slice(1)?.join("\n")}
            </p>
          </div>
        )}
        {(isLoadingContent || !isErrorLikeMsg) && (
          <div
            className={`flex flex-row gap-2 px-2 py-1 items-start mt-3 border border-[#00AEEF] rounded-md bg-[#FAFEFF] ${
              isLoadingContent ? "invisible" : ""
            }`}
          >
            <div>
              <img
                className="xl:h-5 xl:w-5 lg:h-5 lg:w-5 md:h-5 md:w-5 sm:h-8 sm:w-8 h-14 w-14 cursor-pointer"
                src={Info_SVG}
                alt=""
              />
            </div>
            <div className="text-[10px] text-[#414141]">
              This content has been AI generated and should be verified for
              accuracy and completeness. For tailored advice, or clarification,
              please contact our specialist teams on{" "}
              <strong>1300 361 099</strong> or click{" "}
              <span
                onClick={(e) => {
                  e.preventDefault();
                  openModal(true);
                }}
                className="hover:underline text-[#00AEEF] font-semibold cursor-pointer"
              >
                HERE
              </span>{" "}
              to message them now.
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-3 absolute bottom-[-40px] right-1 items-center">
        <div className="relative">
          <img
            className="h-[18px] p-[2px] cursor-pointer"
            src={COPY}
            title="Copy"
            alt="copy"
            onClick={() => {
              handleCopyFile(effectiveMsg); // your existing copy logic
              setCopied(true); // trigger copied state
              setTimeout(() => setCopied(false), 1500); // hide after 1.5 sec
            }}
          />

          {copied && (
            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center">
              <div className="bg-[#2f2f2f] text-white text-[12px] px-2 py-[3px] rounded shadow-md whitespace-nowrap relative">
                Copied!
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-[6px] border-l-transparent border-r-transparent border-t-[#2f2f2f]" />
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 items-center">
          <FaThumbsUp
            title="Like"
            color={liked ? "#00AEEF" : "#B5B5B5"}
            onClick={handleLike}
            className="cursor-pointer"
            size={16}
          />
          <FaThumbsDown
            title="Dislike"
            color={disliked ? "#00AEEF" : "#B5B5B5"}
            onClick={handleDislike}
            className="cursor-pointer"
            size={16}
          />
          <div className="flex items-center gap-3 sm:hidden">
            {speaking && (
              <TbPlayerTrackPrevFilled
                onClick={seekBackward}
                color="#B5B5B5"
                size={16}
                title="Prev 10 sec"
                className="cursor-pointer"
              />
            )}

            {isLoading ? (
              <div className="w-4 h-4 border-2 border-[#B5B5B5] border-t-transparent rounded-full animate-spin"></div>
            ) : speaking ? (
              <IoIosSquare
                title="Stop"
                onClick={handleStop}
                size={16}
                className="cursor-pointer"
                color="#B5B5B5"
              />
            ) : (
              <HiSpeakerWave
                title="Speaker"
                onClick={handleSpeak}
                size={16}
                className="cursor-pointer"
                color="#B5B5B5"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotMessage;
